import { LocalizedError } from '@/lib/i18n/errors';
import { projectConversationEventPreview, type ConversationListRow } from './conversation-event-preview';

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const stringOrMissing = (value: unknown) => value == null || typeof value === 'string';
const integerOrMissing = (value: unknown) => value == null || typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const failed = () => new LocalizedError('inbox', 'list.failed');

export function conversationListFailure(error: unknown, status?: number): LocalizedError {
  const native = { list_unauthorized: [401, 'unauthorized', 'list.unauthorized'], list_invalid_request: [400, 'invalid_request', 'list.invalidRequest'], list_failed: [500, 'internal_error', 'list.failed'] } as const;
  if (record(error) && typeof error.code === 'string' && Object.prototype.hasOwnProperty.call(native, error.code)) {
    const [expectedStatus, alias, key] = native[error.code as keyof typeof native];
    if (status === expectedStatus && error.error === alias) return new LocalizedError('inbox', key, {}, status);
  }
  return failed();
}

/** Native envelope, declared legacy SDK arrays and documented double wrappers.
 * Missing/broken data is a failed read, never evidence of an empty inbox. */
export function readConversationList(payload: unknown, tenant = ''): { rows: ConversationListRow[]; total: number | null } {
  let data = payload, total: number | null = null;
  for (let depth = 0; record(data) && depth < 2; depth++) {
    if ('success' in data && data.success !== true) throw failed();
    if (data.meta != null) {
      if (!record(data.meta)) throw failed();
      if ('total' in data.meta) {
        if (typeof data.meta.total !== 'number' || !Number.isSafeInteger(data.meta.total) || data.meta.total < 0) throw failed();
        if (total !== null && total !== data.meta.total) throw failed();
        total = data.meta.total;
      }
    }
    data = data.data;
  }
  if (!Array.isArray(data)) throw failed();
  const ids = new Set<string>();
  for (const row of data) {
    if (!record(row) || typeof row.id !== 'string' || !row.id.trim() || ids.has(row.id)) throw failed();
    ids.add(row.id);
    if (row.tenant_id != null && row.tenant_id !== tenant) throw failed();
    for (const field of ['channel_id', 'contact_id', 'assigned_user_id', 'assigned_team_id', 'status', 'priority', 'last_message_preview', 'last_message_at', 'snoozed_until', 'waiting_since', 'coordination_state']) {
      if (!stringOrMissing(row[field])) throw failed();
    }
    for (const field of ['is_bot', 'is_muted', 'is_snoozed', 'awaiting_reply']) {
      if (row[field] != null && typeof row[field] !== 'boolean') throw failed();
    }
    if (!integerOrMissing(row.unread_count) || row.tags != null && (!Array.isArray(row.tags) || row.tags.some(tag => typeof tag !== 'string'))) throw failed();
    for (const field of ['contact', 'channel']) {
      const summary = row[field];
      if (summary == null) continue;
      if (!record(summary)) throw failed();
      for (const key of ['id', 'name', 'display_name', 'avatar', 'provider_type']) if (!stringOrMissing(summary[key])) throw failed();
    }
    if (row.presence != null) {
      if (!record(row.presence)) throw failed();
      const viewers = row.presence.viewers;
      if (viewers != null && (!Array.isArray(viewers) || viewers.some(viewer => !record(viewer) || typeof viewer.user_id !== 'string' || typeof viewer.name !== 'string' || !stringOrMissing(viewer.avatar)))) throw failed();
    }
  }
  return { rows: data.map(row => {
    if (!('last_message_event' in row)) return row;
    if (record(row.last_message_event) && row.last_message_event.conversation_id !== undefined && row.last_message_event.conversation_id !== row.id) throw failed();
    return {...row,last_message_event:projectConversationEventPreview(row.last_message_event,row.id as string)};
  }) as ConversationListRow[], total };
}
